import React, { useEffect, useState, useRef, Children, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Check, Sparkles, Phone, User, ChevronLeft, ChevronRight, Stethoscope, Award, TrendingUp, Clock, Shield, Star, X, AlertCircle, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { TestimonialCard } from './components/TestimonialCard';
import { ThankYouPage } from './components/ThankYouPage';
import { initAntiInspect } from './utils/antiInspect';
import { LanguageProvider, useLanguage } from './utils/i18n';
// ============================================================
// DATA CONSTANTS (Static data that doesn't need translation or handled inside component)
// ============================================================
const beforeAfterCases = [{
  id: 1,
  treatment: 'Lipo 360',
  location: 'Miami, FL',
  videoUrl: 'https://cdn.magicpatterns.com/uploads/pHDh5PMD2gcFKT9DR6Ks3h/hf_20260202_030101_0bf12005-668e-4b45-a2f3-7b3b4d0085f0.mp4',
  captionKey: 'c1'
}, {
  id: 2,
  treatment: 'BBL / Body Contouring',
  location: 'Houston, TX',
  videoUrl: 'https://cdn.magicpatterns.com/uploads/r6Jemy4kof1dw99YNZTy3X/hf_20260202_031125_d97931ff-7987-4fb1-83ec-79cd1fdcfb44.mp4',
  captionKey: 'c2'
}, {
  id: 3,
  treatment: 'Mommy Makeover',
  location: 'Phoenix, AZ',
  videoUrl: 'https://cdn.magicpatterns.com/uploads/ucRu3AeSP7Kh4Ds4HttRjm/hf_20260202_034855_e73cf98d-1bbd-49ad-858d-58fb2fcead23.mp4',
  captionKey: 'c3'
}, {
  id: 4,
  treatment: 'Chin Lipo',
  location: 'Miami, FL',
  videoUrl: 'https://cdn.magicpatterns.com/uploads/vcyZVXDFWDSqUa7fJYhCpA/hf_20260202_033540_6e6f4b3c-66cd-476e-84bb-596c42dde3dd.mp4',
  captionKey: 'c4'
}];
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const slideIn = {
  hidden: {
    opacity: 0,
    x: 100
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    x: -100,
    transition: {
      duration: 0.3
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const testimonialsData = [{
  name: 'María G.',
  location: 'Miami, FL • Lipo 360',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't1'
}, {
  name: 'Carmen R.',
  location: 'Houston, TX • Mommy Makeover',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't2'
}, {
  name: 'Ana K.',
  location: 'New York, NY • Face Tightening',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't3'
}, {
  name: 'Laura M.',
  location: 'Las Vegas, NV • BBL',
  image: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't4'
}, {
  name: 'Patricia D.',
  location: 'Phoenix, AZ • Lipo 360',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't5'
}, {
  name: 'Daniela S.',
  location: 'Miami, FL • Body Contouring',
  image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  stars: 5,
  key: 't6'
}];
const cities = [{
  id: 'miami',
  name: 'Miami',
  state: 'FL'
}, {
  id: 'houston',
  name: 'Houston',
  state: 'TX'
}, {
  id: 'newyork',
  name: 'New York',
  state: 'NY'
}, {
  id: 'lasvegas',
  name: 'Las Vegas',
  state: 'NV'
}, {
  id: 'phoenix',
  name: 'Phoenix',
  state: 'AZ'
}, {
  id: 'other',
  name: 'Other City',
  state: 'USA'
}];
const treatments = [{
  id: 'lipo360',
  name: 'Lipo 360',
  icon: '○',
  descKey: 'lipo360'
}, {
  id: 'laserlipo',
  name: 'Laser Lipo',
  icon: '◇',
  descKey: 'laserlipo'
}, {
  id: 'bbl',
  name: 'BBL',
  icon: '◐',
  descKey: 'bbl'
}, {
  id: 'chinlipo',
  name: 'Papada / Chin Lipo',
  icon: '◒',
  descKey: 'chinlipo'
}, {
  id: 'facetightening',
  name: 'Face Tightening',
  icon: '◑',
  descKey: 'facetightening'
}, {
  id: 'bodycontouring',
  name: 'Body Contouring',
  icon: '◎',
  descKey: 'bodycontouring'
}, {
  id: 'mommymakeover',
  name: 'Mommy Makeover',
  icon: '◈',
  descKey: 'mommymakeover'
}];
// ============================================================
// MAIN CONTENT COMPONENT (Wrapped in LanguageProvider in App)
// ============================================================
const formatUSPhone = (value: string): string => {
  // Simply return the raw digits - no formatting to avoid controlled input issues
  return value;
};
// Auto-capitalize each word: "emiro urdaneta" → "Emiro Urdaneta"
const capitalizeName = (name: string): string => {
  return name.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};
function AppContent() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [showThankYou, setShowThankYou] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState(0);
  const [formData, setFormData] = useState({
    city: '',
    treatment: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    whatsappConsent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState({
    hours: 11,
    minutes: 59,
    seconds: 0
  });
  // PIXEL GUARDS
  const viewContentFired = useRef(false);
  const formIntentFired = useRef(false);
  const highEngagementFired = useRef(false);
  // ANTI-INSPECT INIT
  useEffect(() => {
    const cleanup = initAntiInspect();
    return cleanup;
  }, []);
  // META PIXEL INIT
  useEffect(() => {
    if (typeof (window as any).fbq !== 'undefined') return;
    if (typeof document === 'undefined') return;
    try {
      ;
      (function (f: any, b: any, e: string, v: string) {
        if (f.fbq) return;
        const n: any = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e);
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          b.head.appendChild(t);
        }
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      (window as any).fbq('init', '24969120202715605');
      (window as any).fbq('track', 'PageView');
      console.log('PIXEL_PAGEVIEW_FIRED');
    } catch (error) {
      console.error('Failed to load Meta Pixel:', error);
    }
  }, []);
  // TICKER
  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex(prev => (prev + 1) % (t('ticker.messages') as string[]).length);
    }, 4000);
    return () => clearInterval(interval);
  }, [t]);
  // COUNTDOWN
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };else if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };else if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // PIXEL: ViewContent
  useEffect(() => {
    const handleScroll = () => {
      if (viewContentFired.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercent = scrollTop / docHeight * 100;
      if (scrollPercent >= 50) {
        viewContentFired.current = true;
        if (typeof (window as any).fbq === 'function') {
          ;
          (window as any).fbq('track', 'ViewContent', {
            content_name: 'Liposuction Landing',
            content_category: 'Aesthetic Surgery'
          });
          console.log('PIXEL_VIEWCONTENT_FIRED (50% scroll)');
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // PIXEL: HighEngagement
  useEffect(() => {
    const timer = setTimeout(() => {
      if (highEngagementFired.current) return;
      highEngagementFired.current = true;
      if (typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('trackCustom', 'HighEngagement', {
          time_spent: 30
        });
        console.log('PIXEL_HIGH_ENGAGEMENT_FIRED (30s)');
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, []);
  // PIXEL: FormIntent
  const handleFormFieldFocus = () => {
    if (formIntentFired.current) return;
    formIntentFired.current = true;
    if (typeof (window as any).fbq === 'function') {
      ;
      (window as any).fbq('trackCustom', 'FormIntent', {
        form_name: 'Liposuction Form'
      });
      console.log('PIXEL_FORM_INTENT_FIRED');
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // VALIDATION
  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    if (step === 1 && !formData.city) newErrors.city = t('form.step1Error');
    if (step === 2 && !formData.treatment) newErrors.treatment = t('form.step2Error');
    if (step === 3) {
      if (!formData.fullName.trim()) newErrors.fullName = t('form.errorName');
      if (!formData.email.trim()) newErrors.email = t('form.errorEmail');else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('form.errorEmailInvalid');
      const digits = formData.phoneNumber.replace(/\D/g, '');
      // US phone: exactly 10 digits (we already strip country code "1" in onChange)
      if (digits.length !== 10) {
        newErrors.phoneNumber = t('form.errorPhone');
      }
      if (!formData.whatsappConsent) newErrors.whatsappConsent = t('form.errorConsent');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // NAVIGATION
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 1 && typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('trackCustom', 'FormStep1Complete', {
          city: formData.city
        });
      }
      if (currentStep === 2 && typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('trackCustom', 'FormStep2Complete', {
          treatment: formData.treatment,
          city: formData.city
        });
        (window as any).fbq('track', 'InitiateCheckout', {
          content_name: formData.treatment
        });
      }
      if (currentStep === 3) handleSubmit();else setCurrentStep(currentStep + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };
  // SUBMISSION
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // Auto-capitalize name before sending to CRM
    const cleanFullName = capitalizeName(formData.fullName);
    const submissionKey = `dermaclinic_lead_sent_${formData.email}_${formData.phoneNumber.replace(/\D/g, '')}`;
    const lastSubmitTime = sessionStorage.getItem(submissionKey);
    const now = Date.now();
    const twoMinutes = 2 * 60 * 1000;
    if (lastSubmitTime && now - parseInt(lastSubmitTime) < twoMinutes) {
      setShowThankYou(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setIsSubmitting(false);
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || '';
    const utmMedium = urlParams.get('utm_medium') || '';
    const utmCampaign = urlParams.get('utm_campaign') || '';
    const utmTerm = urlParams.get('utm_term') || '';
    const utmContent = urlParams.get('utm_content') || '';
    const pageUrl = window.location.href;
    const nameParts = cleanFullName.split(' ');
    const firstName = nameParts[0] || cleanFullName;
    const lastName = nameParts.slice(1).join(' ') || '';
    const ghlPayload = {
      first_name: firstName,
      last_name: lastName,
      full_name: cleanFullName,
      email: formData.email,
      phone: formData.phoneNumber,
      city: formData.city,
      treatment: formData.treatment,
      language: lang === 'es' ? 'Spanish' : 'English',
      source: 'dermaclinic.blog',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent,
      page_url: pageUrl,
      timestamp: new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        dateStyle: 'short',
        timeStyle: 'short'
      })
    };
    const emailJsData = {
      full_name: cleanFullName,
      email: formData.email || '',
      phone: formData.phoneNumber || '',
      city: formData.city || '',
      treatment: formData.treatment || '',
      whatsapp_consent: formData.whatsappConsent ? 'Yes' : 'No',
      source: 'dermaclinic.blog',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent,
      page_url: pageUrl,
      timestamp: new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        dateStyle: 'short',
        timeStyle: 'short'
      })
    };
    let ghlSuccess = false;
    let emailSuccess = false;
    try {
      const [ghlResponse, emailResponse] = await Promise.allSettled([fetch('https://services.leadconnectorhq.com/hooks/0nuO5M2cKFYDN9cBMlN0/webhook-trigger/9462830b-9cdb-49e0-8715-9393bb976d24', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ghlPayload)
      }), fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: 'service_otq5unq',
          template_id: 'template_uyui7o6',
          user_id: 'U3N2yFeaznDqrv0X6',
          template_params: emailJsData
        })
      })]);
      if (ghlResponse.status === 'fulfilled' && ghlResponse.value.ok) ghlSuccess = true;
      if (emailResponse.status === 'fulfilled' && emailResponse.value.ok) emailSuccess = true;
      if (ghlSuccess || emailSuccess) {
        sessionStorage.setItem(submissionKey, now.toString());
        localStorage.setItem('dermaclinic_lead_data', JSON.stringify({
          fullName: cleanFullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          city: formData.city,
          treatment: formData.treatment,
          whatsappConsent: formData.whatsappConsent,
          timestamp: new Date().toISOString()
        }));
        if (typeof (window as any).fbq === 'function') {
          try {
            ;
            (window as any).fbq('track', 'Lead', {
              content_name: formData.treatment,
              content_category: 'consultation',
              city: formData.city,
              lead_source: 'dermaclinic.blog',
              value: 1,
              currency: 'USD'
            });
            console.log('PIXEL_LEAD_FIRED', {
              treatment: formData.treatment,
              city: formData.city,
              pixelId: '24969120202715605'
            });
          } catch (error) {
            console.warn('Meta Pixel Lead event failed (non-blocking):', error);
          }
        } else {
          console.warn('⚠️ fbq not available — Lead event NOT fired (ad blocker?)');
        }
        setShowThankYou(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        alert('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      alert('Hubo un error al enviar tu solicitud. Por favor verifica tu conexión e intenta de nuevo.');
      setIsSubmitting(false);
      return;
    } finally {
      if (ghlSuccess || emailSuccess) setTimeout(() => {
        setIsSubmitting(false);
      }, 3000);
    }
  };
  if (showThankYou) return <ThankYouPage data-id="element-0" />;
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FEFDFB] font-sans text-[#2E2E2E] antialiased" data-id="element-1">
      {/* 1. URGENCY BAR */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1a1a1a] text-white py-2 px-4 text-center text-xs sm:text-sm font-medium" data-id="element-2">
        <div className="flex items-center justify-center gap-2 animate-pulse" data-id="element-3">
          <Zap className="w-4 h-4 text-[#FFD700]" fill="#FFD700" data-id="element-4" />
          <span data-id="element-5">{t('urgencyBar.offer')}</span>
          <span className="hidden sm:inline text-[#FFD700] font-bold ml-2" data-id="element-6">
            {t('urgencyBar.endsIn')} {timeLeft.hours}h {timeLeft.minutes}m{' '}
            {timeLeft.seconds}s
          </span>
        </div>
      </div>

      {/* Header */}
      <header className="fixed top-[32px] sm:top-[36px] left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E6D3B3]/20 transition-all" data-id="element-7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between" data-id="element-8">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 sm:h-10 w-auto" data-id="element-9" />
          <div className="flex items-center gap-4" data-id="element-10">
            {/* Language Toggle */}
            <div className="flex bg-gray-100 rounded-full p-1 border border-gray-200" data-id="element-11">
              <button onClick={() => setLang('es')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${lang === 'es' ? 'bg-[#C9A961] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-12">
                ES
              </button>
              <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${lang === 'en' ? 'bg-[#C9A961] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13">
                EN
              </button>
            </div>
            <button onClick={scrollToForm} className="hidden sm:flex bg-[#C9A961] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#B09045] transition-colors" data-id="element-14">
              {t('header.cta')}
            </button>
          </div>
        </div>
      </header>

      {/* 2. HERO SECTION */}
      <section className="relative min-h-[85vh] flex items-center justify-center px-4 sm:px-6 pt-32 pb-16 overflow-hidden" data-id="element-15">
        <div className="absolute inset-0" data-id="element-16">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F9F7F2] via-[#FEFDFB] to-[#F5F1E8]" data-id="element-17" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A961' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} data-id="element-18" />
          <div className="absolute inset-0 bg-gradient-radial from-[#C9A961]/10 via-transparent to-transparent" data-id="element-19" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-20">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-21">
            <motion.div variants={fadeInUp} className="inline-block px-4 py-1.5 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 text-[#B09045] text-xs sm:text-sm font-bold mb-6 tracking-wide" data-id="element-22">
              {t('hero.badge')}
            </motion.div>

            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 text-[#2E2E2E]" data-id="element-23">
              {t('hero.headlineStart')}{' '}
              <span className="text-[#C9A961] underline decoration-2 decoration-[#C9A961]/30 underline-offset-4" data-id="element-24">
                {t('hero.headlineHighlight')}
              </span>{' '}
              {t('hero.headlineEnd')}
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-base sm:text-lg md:text-xl text-[#555555] mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-25">
              {t('hero.subheadline')}{' '}
              <span className="font-bold text-[#2E2E2E] bg-[#C9A961]/10 px-1 rounded" data-id="element-26">
                {t('hero.anchorPrice')}
              </span>{' '}
              {t('hero.financing')}
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[#555555] mb-10 font-medium" data-id="element-27">
              <div className="flex items-center gap-1.5" data-id="element-28">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={3} data-id="element-29" />
                <span data-id="element-30">{t('hero.trust1')}</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-31">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={3} data-id="element-32" />
                <span data-id="element-33">{t('hero.trust2')}</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-34">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={3} data-id="element-35" />
                <span data-id="element-36">{t('hero.trust3')}</span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-37">
              <button onClick={scrollToForm} className="w-full sm:w-auto px-8 py-4 bg-[#C9A961] text-white rounded-full font-bold text-lg hover:bg-[#B09045] transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-38">
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-39" />
              </button>
              <button onClick={() => {
              document.getElementById('results')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-[#E6D3B3] text-[#2E2E2E] rounded-full font-semibold hover:border-[#C9A961] hover:text-[#C9A961] transition-all" data-id="element-40">
                {t('hero.ctaSecondary')}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. SOCIAL PROOF TICKER */}
      <div className="bg-[#2E2E2E] text-white/90 py-3 overflow-hidden border-y border-[#C9A961]/30" data-id="element-41">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center" data-id="element-42">
          <div className="flex items-center gap-3" data-id="element-43">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-44" />
            <AnimatePresence mode="wait" data-id="element-45">
              <motion.p key={tickerIndex} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="text-xs sm:text-sm font-medium" data-id="element-46">
                {(t('ticker.messages') as string[])[tickerIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* 4. PAIN SECTION */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white" data-id="element-47">
        <div className="max-w-4xl mx-auto" data-id="element-48">
          <div className="text-center mb-12" data-id="element-49">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-id="element-50">
              {t('painSection.title')}
            </h2>
            <p className="text-[#888888]" data-id="element-51">{t('painSection.subtitle')}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-12" data-id="element-52">
            {[t('painSection.pain1'), t('painSection.pain2'), t('painSection.pain3'), t('painSection.pain4')].map((pain, idx) => <div key={idx} className="flex items-start gap-4 p-6 bg-[#FFF5F5] rounded-2xl border border-red-100" data-id="element-53">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-54">
                  <X className="w-5 h-5 text-red-500" strokeWidth={3} data-id="element-55" />
                </div>
                <p className="text-[#2E2E2E] font-medium" data-id="element-56">{pain}</p>
              </div>)}
          </div>

          <div className="text-center bg-[#F9F7F2] p-8 rounded-3xl border border-[#E6D3B3]" data-id="element-57">
            <h3 className="text-xl font-bold text-[#2E2E2E] mb-3" data-id="element-58">
              {t('painSection.solutionTitle')}
            </h3>
            <p className="text-[#555555] mb-6" data-id="element-59">
              {t('painSection.solutionText')}
            </p>
            <button onClick={scrollToForm} className="text-[#C9A961] font-bold hover:text-[#B09045] flex items-center justify-center gap-2 mx-auto transition-colors" data-id="element-60">
              {t('painSection.solutionCta')} <ArrowRight className="w-4 h-4" data-id="element-61" />
            </button>
          </div>
        </div>
      </section>

      {/* 5. RESULTS SECTION */}
      <section id="results" className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9F7F2]" data-id="element-62">
        <div className="max-w-7xl mx-auto" data-id="element-63">
          <div className="text-center mb-16" data-id="element-64">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#2E2E2E] mb-4" data-id="element-65">
              {t('results.title')}
              <br data-id="element-66" />
              <span className="text-[#C9A961]" data-id="element-67">{t('results.subtitle')}</span>
            </h2>
            <div className="flex items-center justify-center gap-6 text-sm font-medium text-[#555555] mt-6" data-id="element-68">
              <span className="flex items-center gap-1" data-id="element-69">
                <Star className="w-4 h-4 text-[#C9A961] fill-[#C9A961]" data-id="element-70" />{' '}
                {t('results.rating')}
              </span>
              <span className="flex items-center gap-1" data-id="element-71">
                <Shield className="w-4 h-4 text-[#C9A961]" data-id="element-72" />{' '}
                {t('results.satisfaction')}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-73">
            {testimonialsData.slice(0, 3).map((tData, idx) => <div key={idx} className="relative group" data-id="element-74">
                <div className="absolute top-6 right-6 z-10 flex gap-0.5" data-id="element-75">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#C9A961] fill-[#C9A961]" data-id="element-76" />)}
                </div>
                <TestimonialCard quote={t(`testimonials.${tData.key}`)} name={tData.name} location={tData.location} image={tData.image} delay={idx * 0.1} data-id="element-77" />
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-78">
            <button onClick={scrollToForm} className="px-8 py-4 bg-[#2E2E2E] text-white rounded-full font-bold hover:bg-[#1a1a1a] transition-all shadow-lg" data-id="element-79">
              {t('results.cta')}
            </button>
          </div>
        </div>
      </section>

      {/* 6. AUTHORITY/STATS SECTION */}
      <section className="py-16 bg-[#2E2E2E] text-white" data-id="element-80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-81">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/10" data-id="element-82">
            {[{
            label: t('stats.procedures'),
            value: '5,000+'
          }, {
            label: t('stats.experience'),
            value: '15+'
          }, {
            label: t('stats.cities'),
            value: '5'
          }, {
            label: t('stats.satisfaction'),
            value: '98%'
          }].map((stat, idx) => <div key={idx} className="p-4" data-id="element-83">
                <div className="text-4xl sm:text-5xl font-bold text-[#C9A961] mb-2 font-serif" data-id="element-84">
                  {stat.value}
                </div>
                <div className="text-sm sm:text-base text-white/80 font-medium" data-id="element-85">
                  {stat.label}
                </div>
              </div>)}
          </div>
          <p className="text-center text-white/60 mt-12 text-sm tracking-wide uppercase" data-id="element-86">
            {t('stats.tagline')}
          </p>
        </div>
      </section>

      {/* 7. BENEFITS */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white" data-id="element-87">
        <div className="max-w-7xl mx-auto" data-id="element-88">
          <div className="text-center mb-16" data-id="element-89">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-id="element-90">
              {t('benefits.title')}
            </h2>
            <p className="text-[#888888]" data-id="element-91">{t('benefits.subtitle')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-92">
            {[{
            icon: Sparkles,
            title: t('benefits.b1Title'),
            desc: t('benefits.b1Desc')
          }, {
            icon: Clock,
            title: t('benefits.b2Title'),
            desc: t('benefits.b2Desc')
          }, {
            icon: Award,
            title: t('benefits.b3Title'),
            desc: t('benefits.b3Desc')
          }, {
            icon: TrendingUp,
            title: t('benefits.b4Title'),
            desc: t('benefits.b4Desc')
          }].map((benefit, idx) => <div key={idx} className="flex flex-col items-center text-center p-8 bg-white rounded-2xl border border-[#E6D3B3]/30 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-id="element-93">
                <div className="w-16 h-16 rounded-full bg-[#F9F7F2] border border-[#C9A961]/30 flex items-center justify-center mb-6" data-id="element-94">
                  <benefit.icon className="w-8 h-8 text-[#C9A961]" strokeWidth={1.5} data-id="element-95" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-[#2E2E2E]" data-id="element-96">
                  {benefit.title}
                </h3>
                <p className="text-sm text-[#555555] leading-relaxed" data-id="element-97">
                  {benefit.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. FINANCING */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-[#F9F7F2] to-white" data-id="element-98">
        <div className="max-w-5xl mx-auto text-center" data-id="element-99">
          <div className="inline-block px-4 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-bold mb-6 tracking-wide uppercase" data-id="element-100">
            {t('financing.badge')}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#2E2E2E] mb-6" data-id="element-101">
            {t('financing.title')}{' '}
            <span className="text-[#C9A961]" data-id="element-102">{t('financing.price')}</span>
          </h2>
          <p className="text-lg text-[#555555] mb-10 max-w-2xl mx-auto" data-id="element-103">
            {t('financing.subtitle')}
          </p>

          <div className="bg-white p-8 rounded-3xl shadow-xl border border-[#E6D3B3]/50 mb-10" data-id="element-104">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 items-center justify-center mb-8" data-id="element-105">
              <img src="https://cdn.magicpatterns.com/uploads/mbjD9Zum9t5ZWvjctvX8GS/main_logo.png" alt="CareCredit" className="h-8 object-contain mx-auto opacity-80 hover:opacity-100 transition-opacity" data-id="element-106" />
              <img src="https://cdn.magicpatterns.com/uploads/kDSEimj5qpeQvJ4aHi6GSi/Screenshot_2026-01-17_at_9.28.56_PM.png" alt="Affirm" className="h-8 object-contain mx-auto opacity-80 hover:opacity-100 transition-opacity" data-id="element-107" />
              <img src="https://cdn.magicpatterns.com/uploads/88bDZo8Hcqa4d9fVGJ6R2k/Alphaeon_Credit_logo_registered_RGB.webp" alt="Alphaeon" className="h-8 object-contain mx-auto opacity-80 hover:opacity-100 transition-opacity" data-id="element-108" />
              <img src="https://cdn.magicpatterns.com/uploads/4HTZRhR138dtNnNMhzN8TG/Screenshot_2026-04-18_at_5.46.41_PM.png" alt="Cherry" className="h-8 object-contain mx-auto opacity-80 hover:opacity-100 transition-opacity" data-id="element-109" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4 text-sm font-medium text-[#2E2E2E]" data-id="element-110">
              <div className="flex items-center justify-center gap-2" data-id="element-111">
                <Check className="w-4 h-4 text-green-500" data-id="element-112" /> {t('financing.f1')}
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-113">
                <Check className="w-4 h-4 text-green-500" data-id="element-114" /> {t('financing.f2')}
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-115">
                <Check className="w-4 h-4 text-green-500" data-id="element-116" /> {t('financing.f3')}
              </div>
            </div>
          </div>

          <button onClick={scrollToForm} className="px-8 py-4 bg-[#C9A961] text-white rounded-full font-bold text-lg hover:bg-[#B09045] transition-all shadow-lg hover:shadow-xl active:scale-[0.98]" data-id="element-117">
            {t('financing.cta')}
          </button>
        </div>
      </section>

      {/* 9. MORE TESTIMONIALS */}
      <section className="py-16 px-4 sm:px-6 bg-white" data-id="element-118">
        <div className="max-w-7xl mx-auto" data-id="element-119">
          <h2 className="font-serif text-3xl font-bold text-[#2E2E2E] mb-12 text-center" data-id="element-120">
            {t('moreTestimonials.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-121">
            {testimonialsData.slice(3, 6).map((tData, idx) => <div key={idx} className="relative group" data-id="element-122">
                <div className="absolute top-6 right-6 z-10 flex gap-0.5" data-id="element-123">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#C9A961] fill-[#C9A961]" data-id="element-124" />)}
                </div>
                <TestimonialCard quote={t(`testimonials.${tData.key}`)} name={tData.name} location={tData.location} image={tData.image} delay={idx * 0.1} data-id="element-125" />
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9F7F2]" data-id="element-126">
        <div className="max-w-3xl mx-auto" data-id="element-127">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-10 text-center" data-id="element-128">
            {t('faq.title')}
          </h2>
          <div className="space-y-4" data-id="element-129">
            {[1, 2, 3, 4, 5, 6].map((num, idx) => <div key={idx} className="bg-white rounded-2xl border border-[#E6D3B3]/50 overflow-hidden" data-id="element-130">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} className="w-full flex items-center justify-between p-6 text-left font-bold text-[#2E2E2E] hover:bg-gray-50 transition-colors" data-id="element-131">
                  {t(`faq.q${num}`)}
                  {openFaqIndex === idx ? <ChevronUp className="w-5 h-5 text-[#C9A961]" data-id="element-132" /> : <ChevronDown className="w-5 h-5 text-[#888888]" data-id="element-133" />}
                </button>
                <AnimatePresence data-id="element-134">
                  {openFaqIndex === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-135">
                      <div className="p-6 pt-0 text-[#555555] leading-relaxed border-t border-gray-100" data-id="element-136">
                        {t(`faq.a${num}`)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 bg-[#2E2E2E] text-white text-center relative overflow-hidden" data-id="element-137">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-138"></div>
        <div className="relative z-10 max-w-4xl mx-auto" data-id="element-139">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-140">
            {t('finalCTA.title')}
            <br data-id="element-141" />
            <span className="text-[#C9A961]" data-id="element-142">{t('finalCTA.subtitle')}</span>
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-143">
            {t('finalCTA.text')}
          </p>

          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg inline-block mb-10 border border-white/20" data-id="element-144">
            <p className="text-[#FFD700] font-bold text-sm flex items-center gap-2" data-id="element-145">
              <AlertCircle className="w-4 h-4" data-id="element-146" />
              {t('finalCTA.urgency')}
            </p>
          </div>

          <div data-id="element-147">
            <button onClick={scrollToForm} className="px-10 py-5 bg-[#C9A961] text-white rounded-full font-bold text-xl hover:bg-[#B09045] transition-all shadow-[0_0_30px_rgba(201,169,97,0.5)] hover:shadow-[0_0_50px_rgba(201,169,97,0.7)] active:scale-[0.98]" data-id="element-148">
              {t('finalCTA.button')}
            </button>
          </div>
        </div>
      </section>

      {/* 12. FORM WIZARD */}
      <section ref={formRef} className="py-16 sm:py-20 px-4 sm:px-6 bg-white scroll-mt-32" data-id="element-149">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl border border-[#E6D3B3]/30 p-6 sm:p-10 relative overflow-hidden" data-id="element-150">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A961]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-151" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-152">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-3 text-[#2E2E2E]" data-id="element-153">
              {t('form.title')}
            </h2>
            <p className="text-[#555555]" data-id="element-154">{t('form.subtitle')}</p>
          </motion.div>

          {/* Progress Bar */}
          <div className="mb-10" data-id="element-155">
            <div className="flex items-center justify-between mb-2 px-1" data-id="element-156">
              <span className={`text-xs font-bold ${currentStep >= 1 ? 'text-[#C9A961]' : 'text-gray-300'}`} data-id="element-157">
                {t('form.step1')}
              </span>
              <span className={`text-xs font-bold ${currentStep >= 2 ? 'text-[#C9A961]' : 'text-gray-300'}`} data-id="element-158">
                {t('form.step2')}
              </span>
              <span className={`text-xs font-bold ${currentStep >= 3 ? 'text-[#C9A961]' : 'text-gray-300'}`} data-id="element-159">
                {t('form.step3')}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-160">
              <motion.div className="h-full bg-[#C9A961]" initial={{
              width: '33%'
            }} animate={{
              width: `${currentStep / 3 * 100}%`
            }} transition={{
              duration: 0.5
            }} data-id="element-161" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-162">
            {currentStep === 1 && <motion.div key="step1" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-6" data-id="element-163">
                <h3 className="text-xl font-bold text-[#2E2E2E] text-center mb-6" data-id="element-164">
                  {t('form.step1Title')}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-165">
                  {cities.map(city => <button key={city.id} onClick={() => setFormData({
                ...formData,
                city: city.name
              })} className={`group relative p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between ${formData.city === city.name ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-gray-200 hover:border-[#C9A961]/50 bg-white'}`} data-id="element-166">
                      <div className="flex items-center gap-3" data-id="element-167">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.city === city.name ? 'bg-[#C9A961] text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-168">
                          <MapPin className="w-5 h-5" data-id="element-169" />
                        </div>
                        <div data-id="element-170">
                          <div className="font-bold text-[#2E2E2E]" data-id="element-171">
                            {city.id === 'other' ? lang === 'es' ? 'Otra Ciudad' : 'Another City' : city.name}
                          </div>
                          <div className="text-xs text-[#888888]" data-id="element-172">
                            {city.id === 'other' ? lang === 'es' ? 'No estoy en estas ciudades' : "I'm not in these cities" : city.state}
                          </div>
                        </div>
                      </div>
                      {formData.city === city.name && <Check className="w-5 h-5 text-[#C9A961]" data-id="element-173" />}
                    </button>)}
                </div>

                {formData.city && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-3 bg-green-50 text-green-700 text-sm font-medium rounded-lg text-center flex items-center justify-center gap-2" data-id="element-174">
                    <Check className="w-4 h-4" data-id="element-175" />{' '}
                    {formData.city === 'Other City' ? lang === 'es' ? '¡Perfecto! Te contactaremos para coordinar la ubicación más cercana.' : "Perfect! We'll contact you to coordinate the nearest location." : `${t('form.step1Success')} ${formData.city}.`}
                  </motion.div>}

                {errors.city && <p className="text-sm text-red-500 text-center font-medium" data-id="element-176">
                    {errors.city}
                  </p>}
              </motion.div>}

            {currentStep === 2 && <motion.div key="step2" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-6" data-id="element-177">
                <h3 className="text-xl font-bold text-[#2E2E2E] text-center mb-6" data-id="element-178">
                  {t('form.step2Title')}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-179">
                  {treatments.map(treatment => <button key={treatment.id} onClick={() => setFormData({
                ...formData,
                treatment: treatment.name
              })} className={`group relative p-4 rounded-xl border-2 transition-all text-left ${formData.treatment === treatment.name ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-gray-200 hover:border-[#C9A961]/50 bg-white'}`} data-id="element-180">
                      <div className="flex items-center gap-3" data-id="element-181">
                        <div className="text-2xl" data-id="element-182">{treatment.icon}</div>
                        <div data-id="element-183">
                          <div className="font-bold text-[#2E2E2E] text-sm" data-id="element-184">
                            {treatment.name}
                          </div>
                          <div className="text-xs text-[#888888]" data-id="element-185">
                            {t(`treatments.${treatment.descKey}`)}
                          </div>
                        </div>
                      </div>
                      {formData.treatment === treatment.name && <div className="absolute top-4 right-4" data-id="element-186">
                          <Check className="w-5 h-5 text-[#C9A961]" data-id="element-187" />
                        </div>}
                    </button>)}
                </div>

                {formData.treatment && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-3 bg-green-50 text-green-700 text-sm font-medium rounded-lg text-center flex items-center justify-center gap-2" data-id="element-188">
                    <Check className="w-4 h-4" data-id="element-189" /> {formData.treatment}{' '}
                    {t('form.step2Success')}
                  </motion.div>}

                {errors.treatment && <p className="text-sm text-red-500 text-center font-medium" data-id="element-190">
                    {errors.treatment}
                  </p>}
              </motion.div>}

            {currentStep === 3 && <motion.div key="step3" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-6" data-id="element-191">
                <div className="text-center mb-6" data-id="element-192">
                  <h3 className="text-xl font-bold text-[#2E2E2E] mb-2" data-id="element-193">
                    {t('form.step3Title')}
                  </h3>
                  <p className="text-sm text-[#555555]" data-id="element-194">
                    {t('form.step3Subtitle')}
                  </p>
                  <div className="mt-4 inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-bold rounded-full animate-pulse" data-id="element-195">
                    {t('form.step3Urgency')}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-196">
                  {/* Full Name */}
                  <div data-id="element-197">
                    <label className="block text-sm font-bold text-[#2E2E2E] mb-1.5" data-id="element-198">
                      {t('form.labelName')}
                    </label>
                    <div className="relative" data-id="element-199">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-200" />
                      <input type="text" value={formData.fullName} onChange={e => setFormData({
                    ...formData,
                    fullName: e.target.value
                  })} onFocus={handleFormFieldFocus} placeholder={t('form.placeholderName')} className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[#2E2E2E] focus:outline-none focus:border-[#C9A961] focus:bg-white transition-all ${errors.fullName ? 'border-red-400' : 'border-gray-200'}`} style={{
                    fontSize: '16px'
                  }} data-id="element-201" />
                    </div>
                    {errors.fullName && <p className="text-xs text-red-500 mt-1 font-medium" data-id="element-202">
                        {errors.fullName}
                      </p>}
                  </div>

                  {/* Email */}
                  <div data-id="element-203">
                    <label className="block text-sm font-bold text-[#2E2E2E] mb-1.5" data-id="element-204">
                      {t('form.labelEmail')}
                    </label>
                    <div className="relative" data-id="element-205">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 flex items-center justify-center" data-id="element-206">
                        @
                      </div>
                      <input type="email" inputMode="email" autoComplete="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} onFocus={handleFormFieldFocus} placeholder={t('form.placeholderEmail')} className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[#2E2E2E] focus:outline-none focus:border-[#C9A961] focus:bg-white transition-all ${errors.email ? 'border-red-400' : 'border-gray-200'}`} style={{
                    fontSize: '16px'
                  }} data-id="element-207" />
                    </div>
                    {errors.email && <p className="text-xs text-red-500 mt-1 font-medium" data-id="element-208">
                        {errors.email}
                      </p>}
                  </div>

                  {/* Phone Number */}
                  <div data-id="element-209">
                    <label className="block text-sm font-bold text-[#2E2E2E] mb-1.5" data-id="element-210">
                      {t('form.labelPhone')}
                    </label>
                    <div className="relative" data-id="element-211">
                      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-4 pointer-events-none" data-id="element-212">
                        <span className="text-sm font-semibold text-[#555555] mr-1" data-id="element-213">
                          +1
                        </span>
                        <div className="w-px h-5 bg-gray-300 ml-1" data-id="element-214"></div>
                      </div>
                      <input type="tel" inputMode="numeric" autoComplete="tel" value={formData.phoneNumber} onChange={e => {
                    // Only allow digits, max 10 (US local number without country code)
                    const raw = e.target.value.replace(/\D/g, '');
                    // If user typed leading "1" (country code), strip it since we show +1 prefix
                    const cleaned = raw.startsWith('1') && raw.length > 10 ? raw.slice(1) : raw;
                    const limited = cleaned.slice(0, 10);
                    setFormData({
                      ...formData,
                      phoneNumber: limited
                    });
                  }} onFocus={handleFormFieldFocus} placeholder="7861234567" maxLength={10} className={`w-full pl-16 pr-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[#2E2E2E] focus:outline-none focus:border-[#C9A961] focus:bg-white transition-all ${errors.phoneNumber ? 'border-red-400' : 'border-gray-200'}`} style={{
                    fontSize: '16px',
                    letterSpacing: '0.5px'
                  }} data-id="element-215" />
                    </div>
                    {errors.phoneNumber && <p className="text-xs text-red-500 mt-1 font-medium" data-id="element-216">
                        {errors.phoneNumber}
                      </p>}
                  </div>

                  {/* WhatsApp Consent */}
                  <div className="pt-2" data-id="element-217">
                    <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-218">
                      <div className="relative flex-shrink-0 mt-0.5" data-id="element-219">
                        <input type="checkbox" checked={formData.whatsappConsent} onChange={e => setFormData({
                      ...formData,
                      whatsappConsent: e.target.checked
                    })} className="sr-only" data-id="element-220" />
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.whatsappConsent ? 'bg-[#C9A961] border-[#C9A961]' : errors.whatsappConsent ? 'border-red-400' : 'border-gray-300 group-hover:border-[#C9A961]'}`} data-id="element-221">
                          {formData.whatsappConsent && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-222" />}
                        </div>
                      </div>
                      <span className="text-sm text-[#555555] leading-relaxed" data-id="element-223">
                        {t('form.consentText')}
                      </span>
                    </label>
                    {errors.whatsappConsent && <p className="text-xs text-red-500 mt-1.5 font-medium ml-2" data-id="element-224">
                        {errors.whatsappConsent}
                      </p>}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center gap-3" data-id="element-225">
            {currentStep > 1 && <button onClick={handleBack} className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-200 text-[#555555] rounded-xl font-bold hover:border-[#C9A961] hover:text-[#C9A961] transition-all" data-id="element-226">
                <ChevronLeft className="w-5 h-5" strokeWidth={2} data-id="element-227" />
              </button>}

            <button onClick={handleNext} disabled={isSubmitting} className={`flex-1 flex items-center justify-center gap-2 px-8 py-4 bg-[#C9A961] text-white rounded-xl font-bold text-lg hover:bg-[#B09045] transition-all shadow-lg hover:shadow-xl active:scale-[0.98] ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-228">
              {currentStep === 3 ? isSubmitting ? t('form.processing') : t('form.submit') : t('form.continue')}
              {currentStep !== 3 && <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-229" />}
            </button>
          </div>

          {currentStep === 3 && <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-230">
              <div className="flex items-center justify-center gap-4 opacity-60 grayscale hover:grayscale-0 transition-all" data-id="element-231">
                <img src="https://cdn.magicpatterns.com/uploads/mbjD9Zum9t5ZWvjctvX8GS/main_logo.png" alt="CareCredit" className="h-5 object-contain" data-id="element-232" />
                <img src="https://cdn.magicpatterns.com/uploads/kDSEimj5qpeQvJ4aHi6GSi/Screenshot_2026-01-17_at_9.28.56_PM.png" alt="Affirm" className="h-5 object-contain" data-id="element-233" />
                <img src="https://cdn.magicpatterns.com/uploads/4HTZRhR138dtNnNMhzN8TG/Screenshot_2026-04-18_at_5.46.41_PM.png" alt="Cherry" className="h-5 object-contain" data-id="element-234" />
              </div>
              <p className="text-xs text-center text-[#888888] mt-3" data-id="element-235">
                <Shield className="w-3 h-3 inline mr-1" data-id="element-236" />
                {t('form.security')}
              </p>
            </div>}
        </div>
      </section>

      {/* 13. BEFORE/AFTER */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9F7F2]" data-id="element-237">
        <div className="max-w-7xl mx-auto" data-id="element-238">
          <div className="text-center mb-12" data-id="element-239">
            <div className="inline-block px-4 py-1.5 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 text-[#B09045] text-xs font-bold mb-4 tracking-wide uppercase" data-id="element-240">
              {t('beforeAfter.badge')}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-3" data-id="element-241">
              {t('beforeAfter.title')}
            </h2>
            <p className="text-[#888888] max-w-xl mx-auto" data-id="element-242">
              {t('beforeAfter.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-243">
            {beforeAfterCases.map(caseItem => <div key={caseItem.id} className="bg-white rounded-2xl overflow-hidden border border-[#E6D3B3]/40 shadow-md flex flex-col h-full" data-id="element-244">
                {(caseItem as any).videoUrl ? <div className="relative h-48 bg-black overflow-hidden" data-id="element-245">
                    <video src={(caseItem as any).videoUrl} className={`w-full h-full object-cover ${[1, 2, 4].includes(caseItem.id) ? 'scale-125 object-[center_60%]' // Zoom y encuadre bajo para ocultar headers en Lipo 360, BBL y Chin Lipo
              : ''}`} autoPlay muted loop playsInline data-id="element-246" />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-[#C9A961]/90 text-white text-[10px] font-bold rounded uppercase tracking-wider flex items-center gap-1" data-id="element-247">
                      <Zap className="w-3 h-3" fill="white" data-id="element-248" /> Video Result
                    </div>
                  </div> : <div className="grid grid-cols-2" data-id="element-249">
                    <div className="relative" data-id="element-250">
                      <img src={(caseItem as any).beforeImg} alt={`Antes - ${caseItem.treatment}`} loading="lazy" className="w-full h-48 object-cover" data-id="element-251" />
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-[10px] font-bold rounded uppercase tracking-wider" data-id="element-252">
                        {t('beforeAfter.before')}
                      </div>
                    </div>
                    <div className="relative" data-id="element-253">
                      <img src={(caseItem as any).afterImg} alt={`Después - ${caseItem.treatment}`} loading="lazy" className="w-full h-48 object-cover" data-id="element-254" />
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-[#C9A961]/90 text-white text-[10px] font-bold rounded uppercase tracking-wider" data-id="element-255">
                        {t('beforeAfter.after')}
                      </div>
                    </div>
                  </div>}
                <div className="p-5 flex-1 flex flex-col" data-id="element-256">
                  <div className="flex items-center justify-between mb-2" data-id="element-257">
                    <span className="text-sm font-bold text-[#2E2E2E]" data-id="element-258">
                      {caseItem.treatment}
                    </span>
                    <span className="text-xs text-[#888888] flex items-center gap-1" data-id="element-259">
                      <MapPin className="w-3 h-3" data-id="element-260" />
                      {caseItem.location}
                    </span>
                  </div>
                  <p className="text-xs text-[#888888] mt-auto" data-id="element-261">
                    {t(`beforeAfter.${caseItem.captionKey}`)}
                  </p>
                </div>
              </div>)}
          </div>

          <p className="text-[10px] text-[#AAAAAA] text-center mt-8 max-w-lg mx-auto leading-relaxed" data-id="element-262">
            {t('beforeAfter.disclaimer')}
          </p>
        </div>
      </section>

      {/* 15. STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-3 bg-white border-t border-gray-100 shadow-[0_-5px_20px_rgba(0,0,0,0.1)] md:hidden" data-id="element-263">
        <div className="text-center" data-id="element-264">
          <span className="text-sm font-bold text-red-500 animate-pulse flex items-center justify-center gap-2" data-id="element-265">
            <Zap className="w-4 h-4" fill="currentColor" data-id="element-266" />
            {t('stickyMobile.urgency')}
          </span>
        </div>
      </div>
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-267">
      <AppContent data-id="element-268" />
    </LanguageProvider>;
}